import React, { useRef, useState, useEffect } from 'react';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Ensure it always becomes visible if IntersectionObserver is not supported or fails
    let fallbackTimer: ReturnType<typeof setTimeout>;

    if (!window.IntersectionObserver) {
      setIsVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
          clearTimeout(fallbackTimer);
        }
      },
      {
        threshold: 0,
        rootMargin: '200px 0px 200px 0px', // Extra large margin to trigger early
      }
    );

    observer.observe(el);
    
    // Safety fallback: show immediately if IntersectionObserver doesn't fire quickly
    fallbackTimer = setTimeout(() => setIsVisible(true), 150);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}s` }}
      className={`
        transform transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        ${className}
      `}
    >
      {children}
    </div>
  );
};
